import { defineStore, storeToRefs } from 'pinia'
import { useProductStore } from './products';
import { useCustomerStore } from './customer';
import i18n from '../i18n/index.js';

// Store-side translation helper. Stores run outside component setup, so we use
// the global i18n instance directly. Evaluated at getter/call time (render), by
// which point i18n is initialized.
const t = (key) => i18n.global.t(key);

export default defineStore('cart', {
  state: () => ({
    contents: {},
  }),
  getters: {
    // True only when the delivery fee shown to the customer is the real number
    // we will charge. For uber_direct that means a successful live quote is in
    // hand; until the address yields one (e.g. on the Cart screen) the fee is
    // unknown — see `deliveryCost`. Merchant/static services are always known.
    deliveryCostKnown(){
      const cs = useCustomerStore();
      if (!cs.isDeliveryOrder) {
        return false; // no delivery, no charge line at all
      }
      if (!cs.isUberDelivery) {
        return true; // static admin flat fee is known up-front
      }
      const q = cs.uberQuote;
      return q?.status === 'ok' && typeof q.feeCents === 'number' && Number.isFinite(q.feeCents);
    },
    deliveryCost(){
      const cs = useCustomerStore();
      if (!cs.isDeliveryOrder) {
        return 0; // MAGIC: no delivery - no delivery cost
      }
      // Address is outside the merchant's delivery radius: the order can't be
      // placed, so it must never carry a fee — even if Uber returned a live
      // quote. The radius gate and the provider quote are independent checks;
      // both must pass, and the radius gate wins here so totals/tax collapse to
      // zero. See customerStore.deliveryBlocked and deliveryDisplay's 'blocked'.
      if (cs.deliveryBlocked) {
        return 0;
      }
      // The service entry is keyed by the current order type slug; the provider
      // capabilities live in its nested `delivery` object (per frontend.php).
      const service = window?._EZCL?.data?.services?.[cs.orderType];
      const provider = service?.delivery?.provider;
      // Live Uber Direct fee wins when this service uses the uber_direct
      // provider and we have a successful quote. For an uber_direct service
      // WITHOUT a live quote (no address yet, e.g. the Cart screen) the fee is
      // genuinely unknown, so charge 0 here and let `deliveryCostKnown` hide the
      // line — never fall back to the static flat fee, which is merchant-only.
      const q = cs.uberQuote;
      if (provider === 'uber_direct') {
        return (q?.status === 'ok' && typeof q.feeCents === 'number' && Number.isFinite(q.feeCents))
          ? q.feeCents
          : 0;
      }
      // Merchant / static services keep the admin flat fee, bit-identical.
      return service?.cost !== undefined ? Number(service.cost) : 0;
    },
    // Single source of truth for how the delivery fee is presented across every
    // screen (Cart, Customer, Checkout, ThankYou, the address field, the order-
    // type toggle). Returns one descriptor so the status→copy mapping lives in
    // exactly one place instead of being re-derived in each component.
    //
    // state:
    //   'none'     — not a delivery order; render nothing
    //   'flat'     — merchant/static service; fee known up-front
    //   'pending'  — uber_direct, no address yet (e.g. the Cart screen)
    //   'loading'  — uber_direct quote in flight
    //   'quoted'   — uber_direct live fee in hand (amountCents/eta populated)
    //   'no_coverage' — Uber doesn't deliver to this address
    //   'error'    — quote temporarily unavailable; retryable
    //
    // `note` is the single canonical copy per state, so every screen renders
    // identical wording (no per-component switch that can drift). `retryable`
    // tells a screen whether to offer "Try again".
    deliveryDisplay(){
      const cs = useCustomerStore();
      if (!cs.isDeliveryOrder) {
        return { state: 'none', amountCents: 0, eta: null, note: '', retryable: false };
      }
      // Radius gate blocks before anything else: an out-of-zone address can't be
      // delivered to regardless of what Uber quoted, so every screen collapses
      // to the blocked note and no fee. This keeps the radius message and the
      // (now hidden) live quote from contradicting each other on one screen.
      if (cs.deliveryBlocked) {
        return { state: 'blocked', amountCents: 0, eta: null, note: t('delivery.unavailableAddress'), retryable: false };
      }
      if (!cs.isUberDelivery) {
        return { state: 'flat', amountCents: this.deliveryCost, eta: null, note: '', retryable: false };
      }
      const q = cs.uberQuote || {};
      switch (q.status) {
        case 'ok':
          return { state: 'quoted', amountCents: this.deliveryCost, eta: q.etaMinutes ?? null, note: '', retryable: false };
        case 'loading':
          return { state: 'loading', amountCents: 0, eta: null, note: t('delivery.checkingAvailability'), retryable: false };
        case 'no_coverage':
          return { state: 'no_coverage', amountCents: 0, eta: null, note: t('delivery.unavailableAddress'), retryable: false };
        case 'unavailable':
          return { state: 'error', amountCents: 0, eta: null, note: t('delivery.pricingUnavailable'), retryable: true };
        // 'idle' | 'incomplete' | undefined — address not entered yet.
        default:
          return { state: 'pending', amountCents: 0, eta: null, note: t('delivery.calculatedAfterAddress'), retryable: false };
      }
    },
    deliveryTax(){
      if ( useCustomerStore().isDeliveryOrder ) {
        let deliveryTax = useProductStore().getDefaultTaxRates().reduce((acc = 0, taxRate) => {
          // console.debug('Delivery tax', taxRate);  // DEBUG: remove it in production
          return acc + (Math.round(Number(this.deliveryCost) * (Number(taxRate.rate) / 10E6)) + Number(taxRate.taxAmount))
        }, 0);
        // console.debug('Delivery tax', deliveryTax); // DEBUG: remove it in production
        return deliveryTax;
      } else {
        return 0; // MAGIC: no delivery - no delivery tax
      }
    },
    count() {
      return Object.values(this.contents).reduce((acc, lineItem) => {
        return acc + lineItem.unitQty
      }, 0)
    },
    // isEmptyCart() {
    //   return !(Boolean(this.count));
    // },
    total() {
      return Object.values(this.contents).reduce((acc, lineItem) => {
        return acc + lineItem.priceWithModifiers * lineItem.unitQty
      }, 0)
    },
    checkoutTotal(){
      // IMPORTANT: need delivery cost if delivery order type      
      return this.total + this.taxes + this.deliveryCost;
    },
    taxes(){
      // console.debug('Updated tax: start'); // DEBUG: remove it in production
      let taxObjects = Object.values(this.contents).map((lineItem) => this.getLineItemTaxData(lineItem));
      const taxablesList = [this.getDeliveryTaxData(), ...taxObjects];
      const taxesGrouped = {};
      // console.debug(taxablesList); // DEBUG: remove it in production
      taxablesList.forEach(taxableItem => {
        taxableItem.taxIds.forEach(taxId => {
          if (useProductStore().taxRates[taxId] !== undefined) {
            if (taxesGrouped[taxId] === undefined) {
              taxesGrouped[taxId] = {
                rateableSummary: 0,
                flatSummary: 0,
                rated: 0,
                rate: useProductStore().taxRates[taxId].rate,
              }
            }
            // console.debug(taxableItem.rateable, useProductStore().taxRates[taxId].taxAmount);  // DEBUG: remove it in production
            taxesGrouped[taxId].rateableSummary += taxableItem.rateable
            taxesGrouped[taxId].flatSummary += useProductStore().taxRates[taxId].taxAmount
          }
        })
      })
      // console.debug('TAX UPDATED RESULT', taxesGrouped); // DEBUG: remove it in production
      let taxes = Object.values(taxesGrouped).reduce((acc = 0, taxGrouped) => {
        return acc + (Math.round(taxGrouped.rateableSummary * (Number(taxGrouped.rate) / 10E6)) + Number(taxGrouped.flatSummary))
      }, 0);
      // console.debug('Updated tax: pre-end ', taxesGrouped, taxes); // DEBUG: remove it in production
      return taxes;
    },
    updatedTaxes(){
      // console.debug('TAXES', Object.values(this.contents) );  // DEBUG: remove it in production
      let tax = Object.values(this.contents).reduce((acc = 0, lineItem) => {
        // console.debug('total tax:', lineItem, lineItem._uuidhash, this.getLineItemTax(lineItem));  // DEBUG: remove it in production
        return acc + this.getLineItemTax(lineItem)
      }, 0);
      let deliveryTax = (useCustomerStore().isDeliveryOrder && this.deliveryCost && this.deliveryTax) ? this.deliveryTax : 0;
      let totalTax = tax + deliveryTax;
      return totalTax;
    },
  },
  actions: {
    add(lineItem) {
      let productDynamicID = lineItem._uuidhash;
      if (this.contents[productDynamicID]) {
        this.contents[productDynamicID].unitQty += lineItem.unitQty
      }
      else {
        this.contents[productDynamicID] = lineItem
      }
    },
    getDeliveryTaxData() {
      let data = { rateable: 0, taxIds: [] };
      if ( useCustomerStore().isDeliveryOrder && this.deliveryCost && useProductStore()?.getDefaultTaxRates() ) {
        data.taxIds = useProductStore().getDefaultTaxRates().map(taxRate => taxRate.id);
        data.rateable = this.deliveryCost;
      }
      return data;
    },
    getLineItemTaxData(lineItem) {
      let data = { rateable: 0, taxIds: [] };
      if (!lineItem?.item?.id) {
        // console.debug('TAX: Unknown line item: %o', lineItem);  // DEBUG: remove it in production
        return 0;
      }
      const productStore = useProductStore();
      const { taxRates } = storeToRefs(productStore);
      if (!taxRates.value) {
        // console.debug('TAX: Store is not ready yet');  // DEBUG: remove it in production
        return 0;
      }
      data.rateable = lineItem.priceWithModifiers * lineItem.unitQty;
      data.taxIds = productStore.getItemTaxRates(lineItem.item.id).map(taxRate  => taxRate.id);
      return data;
    },
    getLineItemTax(lineItem) {
      if (!lineItem?.item?.id) {
        // console.debug('%s, Unknown line item: %o', arguments.callee.toString(), lineItem);  // DEBUG: remove it in production
        return 0;
      }
      const productStore = useProductStore();
      const { taxRates } = storeToRefs(productStore);
      if (!taxRates.value) {
        // console.debug('Store is not ready yet');  // DEBUG: remove it in production
        return 0;
      }   
      let itemSummaryPrice = lineItem.priceWithModifiers * lineItem.unitQty;
      // console.debug(productStore.getItemTaxRates(lineItem.item.id));  // DEBUG: remove it in production
      return productStore.getItemTaxRates(lineItem.item.id).reduce((acc = 0, taxRate) => {
        return acc + (Math.round(Number(itemSummaryPrice) * (Number(taxRate.rate) / 10E6)) + Number(taxRate.taxAmount))
      }, 0);
    },
    clear() {
      this.contents = {}
    },
    remove(uuidHash) {
      if (!this.contents[uuidHash])
        return

      delete this.contents[uuidHash]
    },
    increase(uuidHash) {
      if (!this.contents[uuidHash])
        return

      this.contents[uuidHash].unitQty += 1
    },
    decrease(uuidHash) {
      if (!this.contents[uuidHash])
        return

      this.contents[uuidHash].unitQty -= 1

      if (this.contents[uuidHash].unitQty === 0)
        delete this.contents[uuidHash]
    },
  },
  persist: {
    key: `_ezcl/${window.location.hostname}/${window['_EZCL']?.data.id}/${window['_EZCL']?.data.pageId}/cart`,
  },
})